import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useCallback } from 'react';
import { cn } from '../../utils/cn';
import { useTheme } from '../../contexts/theme-context';
import { Minus, Plus } from 'lucide-react';
export const NumberField = forwardRef(({ label, error, helperText, size = 'md', required, fullWidth = true, showControls = false, min, max, step = 1, decimalPlaces, value, onChange, prefix, suffix, className, wrapperClassName, labelClassName, disabled, id, ...props }, ref) => {
    const { theme } = useTheme();
    const isCompact = theme?.layoutDensity === 'compact';
    const inputId = id || React.useId();
    // Size classes
    const sizeClasses = {
        sm: isCompact ? 'h-7 text-xs px-2' : 'h-8 text-sm px-2.5',
        md: isCompact ? 'h-8 text-sm px-2.5' : 'h-10 text-sm px-3',
        lg: isCompact ? 'h-10 text-base px-3' : 'h-12 text-base px-4',
    };
    const labelSizeClasses = {
        sm: isCompact ? 'text-xs' : 'text-xs',
        md: isCompact ? 'text-xs' : 'text-sm',
        lg: isCompact ? 'text-sm' : 'text-base',
    };
    const controlSizeClasses = {
        sm: 'w-6 h-6',
        md: 'w-8 h-8',
        lg: 'w-10 h-10',
    };
    const iconSizeClasses = {
        sm: 'h-3 w-3',
        md: 'h-4 w-4',
        lg: 'h-5 w-5',
    };
    const parseValue = useCallback((val) => {
        if (val === undefined || val === '' || val === null)
            return undefined;
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? undefined : num;
    }, []);
    const formatValue = useCallback((val) => {
        if (val === undefined)
            return '';
        if (decimalPlaces !== undefined) {
            return val.toFixed(decimalPlaces);
        }
        return String(val);
    }, [decimalPlaces]);
    const clampValue = useCallback((val) => {
        let result = val;
        if (min !== undefined && result < min)
            result = min;
        if (max !== undefined && result > max)
            result = max;
        return result;
    }, [min, max]);
    const handleChange = useCallback((e) => {
        const inputValue = e.target.value;
        // Allow empty input
        if (inputValue === '' || inputValue === '-') {
            onChange?.(undefined, e);
            return;
        }
        const num = parseFloat(inputValue);
        if (!isNaN(num)) {
            onChange?.(num, e);
        }
    }, [onChange]);
    const handleBlur = useCallback((e) => {
        const num = parseValue(value);
        if (num !== undefined) {
            const clamped = clampValue(num);
            if (clamped !== num) {
                onChange?.(clamped);
            }
        }
        props.onBlur?.(e);
    }, [value, parseValue, clampValue, onChange, props]);
    const increment = useCallback(() => {
        if (disabled)
            return;
        const currentValue = parseValue(value) ?? 0;
        const newValue = clampValue(currentValue + step);
        onChange?.(newValue);
    }, [disabled, value, parseValue, clampValue, step, onChange]);
    const decrement = useCallback(() => {
        if (disabled)
            return;
        const currentValue = parseValue(value) ?? 0;
        const newValue = clampValue(currentValue - step);
        onChange?.(newValue);
    }, [disabled, value, parseValue, clampValue, step, onChange]);
    const displayValue = value !== undefined ? String(value) : '';
    return (_jsxs("div", { className: cn('space-y-1.5', fullWidth && 'w-full', wrapperClassName), children: [label && (_jsxs("label", { htmlFor: inputId, className: cn('block font-medium text-gray-700 dark:text-gray-200', labelSizeClasses[size], labelClassName), children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] })), _jsxs("div", { className: cn('relative flex items-center', showControls && 'gap-1'), children: [showControls && (_jsx("button", { type: "button", onClick: decrement, disabled: disabled || (min !== undefined && (parseValue(value) ?? 0) <= min), className: cn('flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600', 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600', 'text-gray-600 dark:text-gray-300 transition-colors', 'disabled:opacity-50 disabled:cursor-not-allowed', controlSizeClasses[size]), children: _jsx(Minus, { className: iconSizeClasses[size] }) })), _jsxs("div", { className: "relative flex-1", children: [prefix && (_jsx("div", { className: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 dark:text-gray-400", children: _jsx("span", { className: cn(size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm'), children: prefix }) })), _jsx("input", { ref: ref, id: inputId, type: "number", inputMode: "decimal", disabled: disabled, required: required, value: displayValue, onChange: handleChange, onBlur: handleBlur, min: min, max: max, step: step, className: cn(
                                // Base styles
                                'w-full rounded-md border bg-white dark:bg-gray-800 transition-all duration-200', 'placeholder:text-gray-400 dark:placeholder:text-gray-500', 'text-gray-900 dark:text-white', 
                                // Hide number spinners
                                '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none', 
                                // Focus styles
                                'focus:outline-none focus:ring-2 focus:ring-offset-0', 
                                // Size
                                sizeClasses[size], 
                                // Text alignment for numbers (right-align value, left-align placeholder)
                                'text-right placeholder:text-left', 
                                // Prefix/suffix padding
                                prefix && 'pl-8', suffix && 'pr-8', 
                                // States
                                error
                                    ? 'border-red-500 dark:border-red-400 focus:ring-red-500 dark:focus:ring-red-400 focus:border-red-500'
                                    : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400', 
                                // Disabled
                                disabled && 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-700', 
                                // Hover
                                !disabled && !error && 'hover:border-gray-400 dark:hover:border-gray-500', className), ...props }), suffix && (_jsx("div", { className: "absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-500 dark:text-gray-400", children: _jsx("span", { className: cn(size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm'), children: suffix }) }))] }), showControls && (_jsx("button", { type: "button", onClick: increment, disabled: disabled || (max !== undefined && (parseValue(value) ?? 0) >= max), className: cn('flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-600', 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600', 'text-gray-600 dark:text-gray-300 transition-colors', 'disabled:opacity-50 disabled:cursor-not-allowed', controlSizeClasses[size]), children: _jsx(Plus, { className: iconSizeClasses[size] }) }))] }), error && (_jsx("p", { className: cn('text-red-600 dark:text-red-400', isCompact ? 'text-xs' : 'text-sm'), children: error })), helperText && !error && (_jsx("p", { className: cn('text-gray-500 dark:text-gray-400', isCompact ? 'text-xs' : 'text-sm'), children: helperText }))] }));
});
NumberField.displayName = 'NumberField';
